import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  LegacyRef,
} from "react";
import { useTranslation } from "react-i18next";
import styles from "./DeleteAlertModal.module.scss";
import PrimaryButton, { PrimaryButtonType } from "../../common/PrimaryButton";

interface Props {
  onConfirm: () => Promise<void>;
}

export interface DeleteAlertModalRef {
  openModal: () => void;
  closeModal: () => void;
}

const DeleteAlertModal = forwardRef<DeleteAlertModalRef, Props>(
  ({ onConfirm }, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const openModal = useCallback(() => {
      setOpen(true);
      setIsDeleting(false);
    }, []);

    const closeModal = useCallback(() => {
      if (!isDeleting) {
        setOpen(false);
      }
    }, [isDeleting]);

    const stopPropagation = useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
    }, []);

    const handleConfirm = useCallback(async () => {
      setIsDeleting(true);
      try {
        await onConfirm();
        // Success - close modal
        setIsDeleting(false);
        setOpen(false);
      } catch (error) {
        // Error - keep modal open and re-enable buttons
        setIsDeleting(false);
      }
    }, [onConfirm]);

    useImperativeHandle(ref, () => ({
      openModal,
      closeModal,
    }));

    if (!open) return null;

    return (
      <div ref={ref as LegacyRef<HTMLDivElement>} className={styles.modal}>
        <div onClick={closeModal} className={styles.mask} />
        <div onClick={stopPropagation} className={styles.content}>
          {/* Close Button */}
          <div onClick={closeModal} className={styles.close_button}>
            <img src={"/assets/images/svg/cross_icon.svg"} alt="close" />
          </div>

          {/* Content */}
          <div className={styles.modal_body}>
            <h2 className={styles.title}>{t("alert_delete_confirm_title")}</h2>
            <p className={styles.description}>
              {t("alert_delete_confirm_message")}
            </p>
          </div>

          {/* Actions */}
          <div className={styles.actions_container}>
            <PrimaryButton
              onClick={handleConfirm}
              buttonType={
                isDeleting
                  ? PrimaryButtonType.DISABLED
                  : PrimaryButtonType.DANGER
              }
              disabled={isDeleting}>
              <p className={styles.button_text}>
                {isDeleting ? t("loading") : t("delete_confirm")}
              </p>
            </PrimaryButton>
            <PrimaryButton
              onClick={closeModal}
              buttonType={
                isDeleting
                  ? PrimaryButtonType.DISABLED
                  : PrimaryButtonType.PRIMARY_LIGHT
              }
              disabled={isDeleting}>
              <p className={styles.cancel_button}>{t("closed")}</p>
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }
);

DeleteAlertModal.displayName = "DeleteAlertModal";

export default DeleteAlertModal;
